import { Component, computed, inject, input, OnDestroy, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AgrI18nSharedModule, LocaleService } from '@next/core-lib/i18n';
import { Subject, takeUntil } from 'rxjs';

import { GrowthStageRegistration } from '../../models/growth-stage.models';
import { findByCode3Digit, formatBbchDisplayCode, getMajorStageDescriptionKey } from '../../models/bbch-bulb-vegetable-scale';
import { buildCropFieldHistoryTables, CropFieldHistoryTable } from '../../shared/helpers/crop-field-history-table.helper';

export interface CropFieldInfo {
  id: number;
  name: string;
}

interface GrowthStageHistoryRow {
  registrationId: number;
  cropFieldId: number;
  createdAt: Date;
  registrationDate: Date;
  majorStageCode: string;
  majorStageKey: string;
  subphaseCode: string;
  subphaseKey: string;
}

type CropFieldGrowthStageTable = CropFieldHistoryTable<CropFieldInfo, GrowthStageHistoryRow>;

@Component({
  selector: 'app-growth-stage-history',
  standalone: true,
  imports: [CommonModule, MatTableModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, AgrI18nSharedModule],
  templateUrl: './growth-stage-history.component.html',
  styleUrl: './growth-stage-history.component.scss',
})
export class GrowthStageHistoryComponent implements OnDestroy {
  private readonly localeService = inject(LocaleService);
  private readonly destroy$ = new Subject<void>();

  public readonly registrations = input.required<GrowthStageRegistration[]>();
  public readonly cropFields = input.required<CropFieldInfo[]>();
  public readonly isLoading = input<boolean>(false);
  public readonly readOnly = input<boolean>(false);
  public readonly removeRegistration = output<{ cropFieldId: number; growthStageRegistrationId: number }>();

  protected locale = this.localeService.getLocale();
  protected readonly displayedColumns = computed(() =>
    this.readOnly() ? ['date', 'growthStage', 'subphase'] : ['date', 'growthStage', 'subphase', 'actions'],
  );

  protected readonly tables = computed((): CropFieldGrowthStageTable[] => {
    return buildCropFieldHistoryTables(this.cropFields(), this.registrations(), (registration) => this.mapToRow(registration));
  });

  constructor() {
    this.localeService.localeDataChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.locale = this.localeService.getLocale();
    });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected onRemoveRegistration(row: GrowthStageHistoryRow): void {
    if (this.readOnly()) {
      return;
    }

    this.removeRegistration.emit({
      cropFieldId: row.cropFieldId,
      growthStageRegistrationId: row.registrationId,
    });
  }

  private mapToRow(registration: GrowthStageRegistration): GrowthStageHistoryRow {
    const bbchStage = findByCode3Digit(registration.bbchCode);

    const majorStageCode = bbchStage
      ? bbchStage.majorStage.toString()
      : registration.bbchCode.charAt(0);

    const majorStageKey = bbchStage
      ? getMajorStageDescriptionKey(bbchStage.majorStage)
      : '';

    const subphaseCode = formatBbchDisplayCode(bbchStage?.code3Digit ?? registration.bbchCode);
    const subphaseKey = bbchStage?.descriptionKey ?? '';

    return {
      registrationId: registration.id,
      cropFieldId: registration.cropFieldId,
      createdAt: new Date(registration.createdAt),
      registrationDate: new Date(registration.registrationDate),
      majorStageCode,
      majorStageKey,
      subphaseCode,
      subphaseKey,
    };
  }
}
